import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import SignaturePad from 'signature_pad';

import { resizeCanvas } from './resize-canvas.helper';

@Component({
  selector: 'swodoc-node-signature-modal',
  styleUrls: ['swodoc-node-signature.modal.scss'],
  templateUrl: 'swodoc-node-signature.modal.html',
})
export class SwodocNodeSignatureModal implements OnDestroy {
  @ViewChild('signaturPad', { static: true })
  signaturePadCanvas: ElementRef;

  private _sizeChangedSubject = new Subject<Event>();
  private _sizeChangeObservable = this._sizeChangedSubject.asObservable();
  private _signaturePad: SignaturePad;
  private _ngUnsubscribe = new Subject();

  @HostListener('window:resize', ['$event'])
  sizeChange(event: Event) {
    this._sizeChangedSubject.next(event);
  }

  constructor(
    private _modalCtrl: ModalController,
    private _elRef: ElementRef,
    private _loadingCtrl: LoadingController,
    private _translate: TranslateService
  ) {}

  ionViewDidEnter(): void {
    this._signaturePad = new SignaturePad(this.signaturePadCanvas.nativeElement);
    this.initCanvas();
    this._sizeChangeObservable.pipe(debounceTime(100), takeUntil(this._ngUnsubscribe)).subscribe(async () => {
      if (this._signaturePad.isEmpty()) {
        this.initCanvas();
      } else {
        const loading = await this._loadingCtrl.create({
          message: this._translate.instant('audit.node.signature.loading'),
        });
        await loading.present();
        await this.resizeCanvas();
        await loading.dismiss();
      }
    });
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next(null);
    this._ngUnsubscribe.complete();
  }

  public async close(): Promise<void> {
    await this._modalCtrl.dismiss();
  }

  public async dismiss(): Promise<void> {
    await this._modalCtrl
      .dismiss({
        signature: this._signaturePad.isEmpty() ? false : this.cropSignature(this.signaturePadCanvas.nativeElement),
      })
      .catch(error => console.log(error));
  }

  public clear(): void {
    this._signaturePad.clear();
    this.initCanvas();
  }

  private async resizeCanvas(): Promise<void> {
    const canvasImg = this.cropSignature(this.signaturePadCanvas.nativeElement);
    this.initCanvas();
    const ctx = this.signaturePadCanvas.nativeElement.getContext('2d');
    const newWidth = this.signaturePadCanvas.nativeElement.width;
    const newHeight = this.signaturePadCanvas.nativeElement.height;
    const img = new Image();
    await new Promise(resolve => {
      img.onload = () => {
        const { newImgHeight, newImgWidht } = resizeCanvas(img.height, img.width, newHeight, newWidth);
        ctx.drawImage(img, 0, 0, newImgWidht, newImgHeight);
        resolve(null);
      };
      img.src = canvasImg;
    });
  }

  private initCanvas(): void {
    this.signaturePadCanvas.nativeElement.width = this._elRef.nativeElement.offsetWidth;
    this.signaturePadCanvas.nativeElement.height = this._elRef.nativeElement.offsetHeight - 140;
    const ctx = this.signaturePadCanvas.nativeElement.getContext('2d');
    ctx.fillStyle = 'rgb(255,255,255)';
    ctx.fillRect(0, 0, this.signaturePadCanvas.nativeElement.width, this.signaturePadCanvas.nativeElement.height);
  }

  private cropSignature(canvas): string {
    const croppedCanvas: HTMLCanvasElement = document.createElement('canvas');
    const croppedCtx: CanvasRenderingContext2D = croppedCanvas.getContext('2d');
    croppedCanvas.width = canvas.width;
    croppedCanvas.height = canvas.height;

    croppedCtx.drawImage(canvas, 0, 0);

    let w = croppedCanvas.width;
    let h = croppedCanvas.height;
    const pix = { x: [], y: [] };
    const imageData = croppedCtx.getImageData(0, 0, w, h);
    let index = 0;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        index = (y * w + x) * 4;
        if (
          !(imageData.data[index] === 255 && imageData.data[index + 1] === 255 && imageData.data[index + 2] === 255)
        ) {
          pix.x.push(x);
          pix.y.push(y);
        }
      }
    }

    pix.x.sort((a, b) => a - b);
    pix.y.sort((a, b) => a - b);
    const n = pix.x.length - 1;
    w = pix.x[n] - pix.x[0];
    h = pix.y[n] - pix.y[0];

    if (
      this.checkifUndefinedOrNaN(pix.x[0]) ||
      this.checkifUndefinedOrNaN(pix.y[0]) ||
      this.checkifUndefinedOrNaN(w) ||
      this.checkifUndefinedOrNaN(h)
    ) {
      return croppedCanvas.toDataURL('image/jpeg', 1);
    }
    const cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

    croppedCanvas.width = w;
    croppedCanvas.height = h;

    croppedCtx.putImageData(cut, 0, 0);
    return croppedCanvas.toDataURL('image/jpeg', 1);
  }

  private checkifUndefinedOrNaN(number: number): boolean {
    return number === undefined || number === null || isNaN(number);
  }
}
